'use client';

import { Info } from 'lucide-react';
import { type JSX } from 'react';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/lib/contexts/LanguageContext';

interface FinancialYearProps {
  formData: {
    financialYearStart: string;
    financialYearEnd: string;
    financialYearType: string;
    customFinancialYearPeriod: string;
    [key: string]: string | boolean;
  };
  onChange: (section: string, field: string, value: string | boolean) => void;
}

export default function FinancialYearPage({
  formData,
  onChange,
}: Readonly<FinancialYearProps>): JSX.Element {
  const { t } = useLanguage();

  const handleFinancialYearTypeChange = (value: string) => {
    onChange('financialYear', 'financialYearType', value);

    // Auto-set start and end dates based on selection
    const dateRanges: { [key: string]: { start: string; end: string } } = {
      standard1: { start: '01-01', end: '12-31' },
      standard2: { start: '05-01', end: '04-30' },
      standard3: { start: '07-01', end: '06-30' },
      standard4: { start: '09-01', end: '08-31' },
    };

    const range = dateRanges[value];
    if (range) {
      const currentYear = new Date().getFullYear();
      onChange('financialYear', 'financialYearStart', `${currentYear}-${range.start}`);
      onChange(
        'financialYear',
        'financialYearEnd',
        `${value === 'standard2' ? currentYear + 1 : currentYear}-${range.end}`,
      );
    }
  };

  const handleCustomPeriodChange = (value: string) => {
    onChange('financialYear', 'customFinancialYearPeriod', value);

    // Auto-set start and end dates based on custom selection
    const customRanges: { [key: string]: { start: string; end: string } } = {
      custom1: { start: '02-01', end: '01-31' },
      custom2: { start: '03-01', end: '02-28' },
      custom3: { start: '04-01', end: '03-31' },
      custom4: { start: '06-01', end: '05-31' },
      custom5: { start: '08-01', end: '07-31' },
      custom6: { start: '10-01', end: '09-30' },
      custom7: { start: '11-01', end: '10-31' },
      custom8: { start: '12-01', end: '11-30' },
    };

    const range = customRanges[value];
    if (range) {
      const currentYear = new Date().getFullYear();
      onChange('financialYear', 'financialYearStart', `${currentYear}-${range.start}`);
      onChange('financialYear', 'financialYearEnd', `${currentYear + 1}-${range.end}`);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-lg font-medium">{t.financialYearTitle}</h3>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
          {t.financialYearDescription}
        </p>
      </div>

      <RadioGroup
        value={formData.financialYearType}
        onValueChange={handleFinancialYearTypeChange}
        className="space-y-4"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="standard1" id="standard1" />
          <Label htmlFor="standard1" className="cursor-pointer">
            {t.financialYearStandard1}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <RadioGroupItem value="standard2" id="standard2" />
          <Label htmlFor="standard2" className="cursor-pointer">
            {t.financialYearStandard2}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <RadioGroupItem value="standard3" id="standard3" />
          <Label htmlFor="standard3" className="cursor-pointer">
            {t.financialYearStandard3}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <RadioGroupItem value="standard4" id="standard4" />
          <Label htmlFor="standard4" className="cursor-pointer">
            {t.financialYearStandard4}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <RadioGroupItem value="special" id="special" />
          <Label htmlFor="special" className="cursor-pointer font-medium">
            {t.financialYearSpecial}
          </Label>
        </div>
      </RadioGroup>

      {formData.financialYearType === 'special' && (
        <div className="mt-6 space-y-4">
          <Label htmlFor="customPeriod">{t.financialYearCustomLabel}</Label>
          <Select
            value={formData.customFinancialYearPeriod}
            onValueChange={handleCustomPeriodChange}
          >
            <SelectTrigger>
              <SelectValue placeholder={t.financialYearCustom1} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="custom1">{t.financialYearCustom1}</SelectItem>
              <SelectItem value="custom2">{t.financialYearCustom2}</SelectItem>
              <SelectItem value="custom3">{t.financialYearCustom3}</SelectItem>
              <SelectItem value="custom4">{t.financialYearCustom4}</SelectItem>
              <SelectItem value="custom5">{t.financialYearCustom5}</SelectItem>
              <SelectItem value="custom6">{t.financialYearCustom6}</SelectItem>
              <SelectItem value="custom7">{t.financialYearCustom7}</SelectItem>
              <SelectItem value="custom8">{t.financialYearCustom8}</SelectItem>
            </SelectContent>
          </Select>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
            <div className="flex items-start space-x-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
              <p className="text-sm text-blue-800 dark:text-blue-200">{t.financialYearNotice}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
